/**
 * Key-wise comparison and rebasing of editor forms (plan §5.12), shared by
 * the product and category editors. A form is dirty in the keys whose
 * comparable values differ from its base.
 */
export interface IFormModel<F> {
  keys: readonly (keyof F)[];
  /** The comparable value of a key: equal values mean «no change». */
  value: (form: F, key: keyof F) => unknown;
}

export function sameFormKey<F>(
  model: IFormModel<F>,
  a: F,
  b: F,
  key: keyof F
): boolean {
  return (
    JSON.stringify(model.value(a, key)) === JSON.stringify(model.value(b, key))
  );
}

/** Keys whose values differ between two forms. */
export function formChangedKeys<F>(
  model: IFormModel<F>,
  a: F,
  b: F
): (keyof F)[] {
  return model.keys.filter((key) => !sameFormKey(model, a, b, key));
}

/** A deep copy that old WebViews support (no `structuredClone`). */
export function copy<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}

function withKey<F>(form: F, key: keyof F, source: F): F {
  return { ...form, [key]: copy(source[key]) };
}

/**
 * After a save: each key takes the saved value unless the owner changed it
 * while the request was in flight (it no longer equals what was sent).
 */
export function formRebaseAfterSave<F>(
  model: IFormModel<F>,
  form: F,
  sent: F,
  saved: F
): F {
  return model.keys.reduce(
    (next, key) =>
      sameFormKey(model, form, sent, key) ? withKey(next, key, saved) : next,
    form
  );
}

export interface IRemoteRebase<F> {
  base: F;
  form: F;
  /** Dirty keys another device changed too. */
  conflicts: (keyof F)[];
}

/** Another device changed the entity: clean keys follow it, dirty ones stay. */
export function formRebaseOnRemote<F>(
  model: IFormModel<F>,
  base: F,
  form: F,
  remote: F
): IRemoteRebase<F> {
  const conflicts: (keyof F)[] = [];
  let next = form;
  for (const key of model.keys) {
    if (sameFormKey(model, base, remote, key)) continue;
    if (sameFormKey(model, base, form, key)) next = withKey(next, key, remote);
    else conflicts.push(key);
  }
  return { base: remote, form: next, conflicts };
}
